import { useEffect, useState } from "react";
import PropTypes from "prop-types";

/** Lib Imports */
import { Timeline } from "primereact/timeline";
import moment from "moment";

/** ENUM */
import JobRequisitionStatus from '../../../enums/JobRequisitionStatus';

/** RTK Query */
import { useLazyGetDataQuery } from "../../../app/services/commonApi";

/** CSS */
import "./RequisitionHistory.scss";
import { Card } from "primereact/card";

const getApprovalLevelName = (level) => {
    switch (level) {
        case 1:
            return "Interviewer";
        case 2:
            return "HR";
        case 3:
            return "Admin";
        default:
            return "Requester";
    }
};
const getStatusClass = (statusId) => {
    switch (statusId) {
        case JobRequisitionStatus.DRAFT: return 'status-draft';
        case JobRequisitionStatus.PENDING_APPROVAL: return 'status-pending';
        case JobRequisitionStatus.APPROVED: return 'status-approved';
        case JobRequisitionStatus.REJECTED: return 'status-rejected';
        case JobRequisitionStatus.CANCELLED: return 'status-rejected';
        case JobRequisitionStatus.ON_HOLD: return 'status-pending';
        default: return 'status-draft';
    }
};

const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

const getStatusIcon = (statusClass) => {
    switch (statusClass) {
        case 'status-approved': return 'pi pi-check';
        case 'status-rejected': return 'pi pi-times';
        case 'status-pending': return 'pi pi-sync';
        case 'status-draft': return 'pi pi-file-edit';
        default: return 'pi pi-history';
    }
};

const RequisitionHistory = ({ jobRequisitionId }) => {
    const [historyData, setHistoryData] = useState([]);
    const [getRequisitionHistory,
        {
            data: requisitionHistoryData,
            isFetching: requisitionHistoryFetching,
            isSuccess: requisitionHistorySuccess
        }] = useLazyGetDataQuery();

    useEffect(() => {
        if (jobRequisitionId) {
            getRequisitionHistory({
                url: '/JobRequisition/GetRequisitionHistory',
                data: Number(jobRequisitionId)
            });
        }
    }, [jobRequisitionId, getRequisitionHistory]);

    useEffect(() => {
        if (!requisitionHistoryFetching && requisitionHistorySuccess && requisitionHistoryData) {
            const sortedData = [...requisitionHistoryData.data].reverse();
            const uniqueData = [];
            const seen = new Set();

            sortedData.forEach(item => {
                const key = item.description + item.currentStatus + item.changedAt + item.approvalLevel;

                if (!seen.has(key)) {
                    seen.add(key);
                    uniqueData.push(item);
                }
            });

            const mappedData = uniqueData.map((item) => ({
                approver: item.changedByName || `User ${item.changedBy}`,
                approverRole: item.changedByRoleName || '',
                level: item.approvalLevel,
                levelName: getApprovalLevelName(item.approvalLevel),
                action: item.description || 'Job Requisition Status Updated',
                statusFrom: item.previousStatusName || '-',
                statusTo: item.currentStatusName || '-',
                comments: item.rejectionReason || '',
                timestamp: moment(item.changedAt).format('DD MMM YYYY, hh:mm A'),
                statusClass: getStatusClass(item.currentStatus),
                statusIcon: getStatusIcon(getStatusClass(item.currentStatus))
            }));

            setHistoryData(mappedData);
        } else {
            setHistoryData([]);
        }

    }, [requisitionHistoryData, requisitionHistoryFetching, requisitionHistorySuccess]);

    // ✅ Marker UI
    const customizedMarker = (item) => {
        return (
            <span className={`custom-marker ${item.statusClass}`}>
                <i className={item.statusIcon} />
            </span>
        );
    };

    const customizedContent = (item) => {
        const initials = getInitials(item.approver);

        return (
            <Card className={`history-timeline-card ${item.statusClass}-card`}>
                <div className="history-content">
                    <div className="history-header">
                        <div className="approver-part">
                            <div className="approver-avatar">
                                <span>{initials}</span>
                            </div>
                            <div className="approver-meta">
                                <span className="approver-name">{item.approver}</span>
                                {item.approverRole && (
                                    <span className="approver-role-badge">{item.approverRole}</span>
                                )}
                            </div>
                        </div>

                        <span className="history-timestamp">
                            <i className="pi pi-clock" />
                            <span>{item.timestamp}</span>
                        </span>
                    </div>

                    <div className="history-action-bar">
                        <span className="action-title">{item.action}</span>
                    </div>

                    <div className="history-details">
                        <div className="status-flow-container">
                            <span className="detail-label">Status:</span>
                            <span className="status-pill status-from">{item.statusFrom}</span>
                            <i className="pi pi-arrow-right flow-arrow" />
                            <span className={`status-pill status-to ${item.statusClass}`}>{item.statusTo}</span>
                        </div>

                        <div className="approval-level-container">
                            <span className="detail-label">Current Approval Level:</span>
                            <span className="level-pill">
                                <i className="pi pi-shield" />
                                <strong>{item.levelName}</strong>
                            </span>
                        </div>
                    </div>

                    {item.comments && (
                        <div className="history-comment-box">
                            <i className="pi pi-comment comment-icon" />
                            <div className="comment-content">
                                <span className="comment-label">Reason / Comments:</span>
                                <p className="comment-text">{item.comments}</p>
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    const renderHistory = () => {
        if (requisitionHistoryFetching) {
            return (
                <div className="history-loading-state">
                    <i className="pi pi-spin pi-spinner" />
                    <span>Loading requisition history...</span>
                </div>
            );
        }

        if (historyData.length > 0) {
            return (
                <Timeline
                    value={historyData}
                    align="left"
                    className="customized-timeline"
                    marker={customizedMarker}
                    content={customizedContent}
                />
            );
        }

        return (
            <div className="history-empty-state">
                <i className="pi pi-history" />
                <h5>No Activity Found</h5>
                <p>There is no requisition history recorded for this entry yet.</p>
            </div>
        );
    };

    return (
        <div className="requisition-history-container">
            {renderHistory()}
        </div>
    );
};

RequisitionHistory.propTypes = {
    jobRequisitionId: PropTypes.number.isRequired,
};

export default RequisitionHistory;